import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import SkillForm from '../forms/SkillForm';
import useSkills from '../../hooks/useSkills';
import useProfile from '../../hooks/UseProfile';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import SkillsList from './SkillsList';

const SkillsSection = ({ cv, compact = false, onAddClick, triggerAdd, readonly = false }) => {
    // useSkills hook now uses React Query for data fetching and mutations
    // It automatically handles loading states, caching, and refetching
    const { skills, addSkill, updateSkill, deleteSkill, isLoading } = useSkills(cv?.id);
    const { refetch: refreshProfile } = useProfile();
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const prevTriggerRef = useRef(0);

    const handleAdd = () => {
        setSelectedSkill(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (triggerAdd && triggerAdd !== prevTriggerRef.current) {
            prevTriggerRef.current = triggerAdd;
            handleAdd();
        }
    }, [triggerAdd]);

    const handleEdit = (skill) => {
        setSelectedSkill(skill);
        setIsModalOpen(true);
    };

    const handleDelete = (skill) => {
        setSelectedSkill(skill);
        setIsDeleteModalOpen(true);
    };


    const handleConfirmDelete = async () => {
        if (selectedSkill) {
            try {
                // deleteSkill is now a React Query mutation
                // It will automatically invalidate and refetch the skills query on success
                await deleteSkill(selectedSkill.id);
                // We still need to refresh the profile as it might contain aggregated data
                await refreshProfile();
                setIsDeleteModalOpen(false);
            } catch (err) {
                setError('Failed to delete skill');
            }
        }
    };

    const content = (
        <>
            {error && (
                <Typography color="error" className="mb-4">
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading skills...</Typography>
                </Box>
            ) : (
                <SkillsList 
                    skills={skills} 
                    onEdit={readonly ? undefined : handleEdit} 
                    onDelete={readonly ? undefined : handleDelete}
                    compact={compact}
                />
            )}

            <CustomModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                title={selectedSkill ? 'Edit Skill' : 'Add New Skill'}
            >
                <SkillForm 
                    id={selectedSkill?.id}
                    onClose={() => setIsModalOpen(false)}
                    cvId={cv?.id}
                />
            </CustomModal>

            <DeleteDialog
                url={`/api/skills/${selectedSkill?.id}`}
                isOpen={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                notifyDone={handleConfirmDelete}
                message="Are you sure you want to delete this skill?"
            />
        </>
    );

    if (compact) {
        return content;
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Skills
                </Typography>
                {!readonly && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {content}
        </Paper>
    );
};

export default SkillsSection; 